import http from "@/utils/http";
import { useEstimateStore } from "../stores/estimateStore";
import { Estimate, Pricing } from "@/app/domain";
import { generateCode } from "./common";

import dayjs from "dayjs";

export async function useLogin(email: string, password: string) {
    return await http.post("/auth/login", { email: email, password: password });
}

export async function useLogout() {
    return await http.post("/auth/logout");
}

export async function useLoginStatus() {
    return await http.get("/auth/status");
}

export async function getEstimate(estimateCode: string) {
    const resp = (await http.get(`/api/estimates/${estimateCode}`)) as any;
    if (typeof resp.data === "object") {
        return resp.data;
    }
    try {
        return JSON.parse(resp.data);
    } catch (e) {
        console.error("Failed to parse estimate response:", e);
        return null;
    }
}

export async function postEstimate(
    estimate: object,
    estimateCode,
    isNew: boolean = false,
): any {
    let resp = null;
    try {
        console.debug("api save start");
        if (isNew) {
            // create
            resp = await http.post(`/api/estimates`, estimate);
        } else {
            // update
            resp = await http.put(`/api/estimates/${estimateCode}`, estimate);
        }
        console.debug("api save finish");
        return resp?.data;
    } catch (error) {
        console.error(error);
    }
    return null;
}

export async function postHubspot(estimateCode: string): any {
    try {
        const resp = await http.post(
            `/api/estimates/${estimateCode}/hubspot`,
            estimateCode,
        );
        return resp?.data;
    } catch (error) {
        console.error(error);
    }
    return null;
}

export async function useDictSaver(name: string, data: object[]): any {
    let resp = null;
    try {
        resp = await http.put(`/api/dicts/${name}`, data);
        return resp?.data;
    } catch (error) {
        console.error(error);
    }
    return null;
}

export async function useDictLoaderByDate(
    name: string,
    date: string,
    dateWithTime?: any,
): any {
    let resp = null;
    const timestamp = dateWithTime
        ? new Date(dateWithTime).getTime()
        : new Date().getTime();

    try {
        resp = await http.get(
            `/api/dicts-by-date/${name}/${date}?t=${timestamp}`,
        );
        return resp?.data;
    } catch (error) {
        console.error(error);
    }
    return null;
}

export async function useDictLoader(name: string): any {
    const estimateStore = useEstimateStore();
    if (estimateStore?.estimate?.info?.priceDateFormated) {
        return useDictLoaderByDate(
            name,
            estimateStore?.estimate?.info?.priceDateFormated,
            estimateStore?.estimate?.info?.priceDate,
        );
    }
    let resp = null;
    try {
        resp = await http.get(`/api/dicts/${name}`);
        return resp?.data;
    } catch (error) {
        console.error(error);
    }
    return null;
}

export async function useDiscount(): any {
    let resp = null;
    try {
        resp = await http.get(`/api/discount`);
        return resp;
    } catch (error) {
        console.error(error);
    }

    return null;
}

export async function useUsersLoader(role: string): any {
    if (!["estimators", "managers"].includes(role)) {
        console.error(`dict ${role} dosen't exist `);
        return null;
    }
    let resp = null;
    try {
        resp = await http.get(`/api/${role}`);
        return resp?.data;
    } catch (error) {
        console.error(error);
    }
    return null;
}

export async function useClientsDictLoader(): any {
    let resp = null;
    try {
        resp = await http.get(`/api/clients`);
        return resp?.data;
    } catch (error) {
        console.error(error);
    }
    return null;
}

export async function updateUser(data): any {
    let resp = null;
    try {
        resp = await http.post(`/api/clients`, data);
        return resp?.data;
    } catch (error) {
        console.error(error);
    }
    return null;
}

export const makeEstimateDefaultData = (): Estimate => ({
    title: "",
    started: new Date(),
    code: generateCode(),
    priceDate: Date.now(),
    priceDateFormated: dayjs().format("YYYY-MM-DD"),
    estimateNo: "",
    // estimateNo: dayjs().format("YYMMDD-hm"),
    trackCode: "",
    dealTypeCode: "",
    installationPlaceCode: "",
    installationUsageCode: "",
    installedSystemCode: "",
    colorCode: "",
    delivery: true,
    deliverySum: 0,
    deliveryType: "",
    isIncludeCombinedDeliveryInPrice: false,
    isShippingAndBillingEqual: false,
    installation: false,
    shippingZip: "",
    shippingState: "",
    billingZip: "",
    billingState: "",
    billingAddress: "",
    estimatorCode: "",
    estimatorName: "",
    managerCode: null,
    multiplicator: 1,
    client: {
        name: "",
        businessName: "",
        email: "",
        phone: "",
        shippingAddress: "",
        billingAddress: "",
        billingZip: "",
        billingState: "",
    },
    progress: {
        status: "DRAFT",
        updated: new Date(),
        validatedPage: 0,
        activePage: 1,
        activeSpaceCode: "",
        activeStructureCode: "",
        activeMountsCode: "",
        activeFillerCode: "",
        activeDoorCode: "",
        isSaved: false,
    },
    discountPercent: 10,
    discountSum: 0,
    subtotal: new Pricing(),
    estimatePrice: new Pricing(),
    estimatePriceWithoutExtra: new Pricing(),
    halfInstallation: 0,
    installationSum: 0,
    overChargeSum: 0,
    halfInstallationWithDiscount: 0,
    isHalfInstallation: true,
    totalSquareFt: null,
    priceBreakdownInQuote: false,
    taxLimit: false,
    taxStatus: false,
    totalWithoutTax: 0,
    totalWithTax: 0,
    taxPercent: 0,
    taxSum: 0,
    totalGlassWeight: 0,
    isTaxExempt: false,
    customerNotice: "",
});
